import type ArrayProxy from '@ember/array/proxy';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency';
import type CompanyModel from 'garaje/models/company';
import type DeskLocationModel from 'garaje/models/desk-location';
import type FeatureModel from 'garaje/models/feature';
import type LocationModel from 'garaje/models/location';
import type SubscriptionPlanModel from 'garaje/models/subscription-plan';
import type AjaxService from 'garaje/services/ajax';
import type BannerManagerService from 'garaje/services/banner-manager';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type { AnySubscriptionModel, MaybeSubscriptionModel } from 'garaje/services/state';
import { APP, SUBSCRIPTION_SCOPE } from 'garaje/utils/enums';
import { TrackedArray } from 'tracked-built-ins';
import type { Constructor } from 'type-fest';

type SubscriptionKey =
  | 'Visitors'
  | 'Deliveries'
  | 'Rooms'
  | 'Desks'
  | 'Workplace'
  | 'Platform'
  | 'Reservations'
  | 'Screens'
  | 'Connect'
  | 'Emno'
  | 'Vfd';

// Members provided by the wrapping StateService as route-scope-aware getters.
// The mixin can't `declare` them here without colliding with the host's
// accessor implementations (TS2611), so reads cast `this` through this alias.
type HostScopedReads = {
  features?: FeatureModel | null;
  visitorsSubscription?: MaybeSubscriptionModel;
  desksSubscription?: MaybeSubscriptionModel;
  reservationsSubscription?: MaybeSubscriptionModel;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
function withSubscriptionStateFunctionality<TBase extends Constructor<any>>(Base: TBase) {
  class withSubscriptionStateFunctionality extends Base {
    declare companyVisitorsSubscription?: MaybeSubscriptionModel;
    declare locationVisitorsSubscription?: MaybeSubscriptionModel;
    declare companyDesksSubscription?: MaybeSubscriptionModel;
    declare locationDesksSubscription?: MaybeSubscriptionModel;
    declare companyDeliveriesSubscription?: MaybeSubscriptionModel;
    declare locationDeliveriesSubscription?: MaybeSubscriptionModel;
    declare companyRoomsSubscription?: MaybeSubscriptionModel;
    declare locationRoomsSubscription?: MaybeSubscriptionModel;
    declare companyWorkplaceSubscription?: MaybeSubscriptionModel;
    declare locationWorkplaceSubscription?: MaybeSubscriptionModel;
    declare companyPlatformSubscription?: MaybeSubscriptionModel;
    declare locationPlatformSubscription?: MaybeSubscriptionModel;
    declare companyReservationsSubscription?: MaybeSubscriptionModel;
    declare locationReservationsSubscription?: MaybeSubscriptionModel;
    declare companyConnectSubscription?: MaybeSubscriptionModel;
    declare locationConnectSubscription?: MaybeSubscriptionModel;
    declare companyEmnoSubscription?: MaybeSubscriptionModel;
    declare locationEmnoSubscription?: MaybeSubscriptionModel;
    declare companyVfdSubscription?: MaybeSubscriptionModel;
    declare locationVfdSubscription?: MaybeSubscriptionModel;
    declare companyScreensSubscription?: MaybeSubscriptionModel;
    declare locationScreensSubscription?: MaybeSubscriptionModel;
    declare companyFeatures?: FeatureModel | null;
    declare locationFeatures?: FeatureModel | null;

    declare realDeliveriesSubscription?: MaybeSubscriptionModel;
    declare realRoomsSubscription?: MaybeSubscriptionModel;
    declare realDesksSubscription?: MaybeSubscriptionModel;

    @tracked hasAnySubscriptions = false;

    declare currentLocation?: LocationModel | null;
    declare currentCompany: CompanyModel | null;

    declare allPlans: ArrayProxy<SubscriptionPlanModel> | null;

    declare allDeskLocations: DeskLocationModel[];
    declare activeDeskLocations: DeskLocationModel[];

    declare _companyMeta: unknown;

    @service declare featureFlags: FeatureFlagsService;
    @service declare store: StoreService;
    @service declare ajax: AjaxService;
    @service declare bannerManager: BannerManagerService;
    @service declare featureConfig: FeatureConfigService;
    @service declare flashMessages: FlashMessagesService;

    initSubscriptionStateTask = restartableTask(
      async (
        incomingLocation?: LocationModel,
        preloadedSubscriptions?: AnySubscriptionModel[],
        preloadedFeatures?: FeatureModel | null,
      ) => {
        const { featureFlags, currentCompany } = this;

        if (!currentCompany) {
          return;
        }

        if (!this.currentLocation) {
          this.currentLocation = incomingLocation;
        }
        // Capture after the incomingLocation assignment so all three
        // branches see the same value.
        const location = this.currentLocation;

        // Precedence: locationBilling > MPB > default.
        if (featureFlags.isEnabled('locationBilling') && location) {
          await location.hasMany('locationSubscriptions').reload();
          const [locationSubscriptions, allPlans, locationScopedFeatures] = await Promise.all([
            (async () => (await location.locationSubscriptions).slice())(),
            this.store.findAll('subscription-plan'),
            this.store.queryRecord('feature', {
              filter: { company_id: currentCompany.id, location_id: location.id },
            }),
          ]);

          this._assignSubscriptionFields('location', locationSubscriptions);
          // Under locationBilling there is no separate company-scoped
          // subscription source — locationSubscriptions IS the data, so the
          // company-scoped slot mirrors it (same pattern as companyFeatures
          // = locationScopedFeatures below).
          this._assignSubscriptionFields('company', locationSubscriptions);
          this._assignRealSubscriptionFields(locationSubscriptions);
          this.hasAnySubscriptions = locationSubscriptions.length > 0;
          this.allPlans = allPlans;
          // Preserves pre-MPB behavior: the location-scoped
          // record is exposed regardless of route
          this.companyFeatures = locationScopedFeatures;
          this.locationFeatures = locationScopedFeatures;
        } else if (featureFlags.isEnabled('company-has-mixed-plan-billing') && location) {
          // Mirrors envoy-web's CompanyFeatures#billing_api_client.
          // allPlans is needed for LocationSubscription's
          // @alias('subscriptionPlan.*') getters when plans aren't sideloaded.
          const companyFeaturesPromise =
            preloadedFeatures !== undefined
              ? Promise.resolve(preloadedFeatures)
              : this.store.queryRecord('feature', { filter: { company_id: currentCompany.id } });

          const scopedSubscriptionsPromise = this.store
            .query('scoped-subscription', {
              filter: {
                scope: SUBSCRIPTION_SCOPE.MIXED_PLAN,
                company_id: currentCompany.id,
                location_id: location.id,
              },
            })
            .then((r) => r.slice());

          // Since two separate subscription calls are made for MPB, allow scoped
          // pages to still work even if loading Company plans fails
          const companySubscriptionsPromise =
            preloadedSubscriptions !== undefined
              ? Promise.resolve(preloadedSubscriptions)
              : this.store
                  .query('subscription', { filter: { company_id: currentCompany.id } })
                  .then((r) => r.slice())
                  .catch(() => [] as AnySubscriptionModel[]);

          const [scopedSubscriptions, companySubscriptions, allPlans, companyFeatures, locationFeatures] =
            await Promise.all([
              scopedSubscriptionsPromise,
              companySubscriptionsPromise,
              this.store.findAll('subscription-plan'),
              companyFeaturesPromise,
              this.store.queryRecord('feature', {
                filter: { company_id: currentCompany.id, location_id: location.id },
              }),
            ]);

          this._assignSubscriptionFields('location', scopedSubscriptions);
          this._assignSubscriptionFields('company', companySubscriptions);
          this._assignRealSubscriptionFields(scopedSubscriptions);
          this.hasAnySubscriptions = scopedSubscriptions.length > 0 || companySubscriptions.length > 0;
          this.allPlans = allPlans;
          this.companyFeatures = companyFeatures ?? null;
          this.locationFeatures = locationFeatures;
        } else {
          let subscriptions = preloadedSubscriptions;
          if (!subscriptions) {
            const subscriptionsFilter = { filter: { company_id: currentCompany.id } };
            subscriptions = (await this.store.query('subscription', subscriptionsFilter)).slice();
          }

          this._assignSubscriptionFields('company', subscriptions);
          // No per-location data in the default branch; clear any stale
          // location-scoped slots from a prior MPB/locationBilling boot.
          this._assignSubscriptionFields('location', []);
          this._assignRealSubscriptionFields(subscriptions);
          this.hasAnySubscriptions = subscriptions.length > 0;
          this.companyFeatures = preloadedFeatures ?? null;
          if (preloadedFeatures === undefined) {
            const featuresFilter = { filter: { company_id: currentCompany?.id } };
            this.companyFeatures = await this.store.queryRecord('feature', featuresFilter);
          }
          this.locationFeatures = null;
        }

        if ((this as HostScopedReads).visitorsSubscription) {
          void this.checkRestrictionsTask.perform();
        }

        if (this.shouldLoadDeskLocations) {
          void this.setDeskLocationsTask.perform();
        }
      },
    );

    _assignSubscriptionFields(scope: 'company' | 'location', subscriptions: AnySubscriptionModel[]): void {
      const { featureFlags } = this;
      const setters: Record<SubscriptionKey, (sub: MaybeSubscriptionModel) => void> =
        scope === 'company'
          ? {
              Visitors: (sub) => (this.companyVisitorsSubscription = sub),
              Deliveries: (sub) => (this.companyDeliveriesSubscription = sub),
              Rooms: (sub) => (this.companyRoomsSubscription = sub),
              Desks: (sub) => (this.companyDesksSubscription = sub),
              Workplace: (sub) => (this.companyWorkplaceSubscription = sub),
              Platform: (sub) => (this.companyPlatformSubscription = sub),
              Reservations: (sub) => (this.companyReservationsSubscription = sub),
              Screens: (sub) => (this.companyScreensSubscription = sub),
              Connect: (sub) => (this.companyConnectSubscription = sub),
              Emno: (sub) => (this.companyEmnoSubscription = sub),
              Vfd: (sub) => (this.companyVfdSubscription = sub),
            }
          : {
              Visitors: (sub) => (this.locationVisitorsSubscription = sub),
              Deliveries: (sub) => (this.locationDeliveriesSubscription = sub),
              Rooms: (sub) => (this.locationRoomsSubscription = sub),
              Desks: (sub) => (this.locationDesksSubscription = sub),
              Workplace: (sub) => (this.locationWorkplaceSubscription = sub),
              Platform: (sub) => (this.locationPlatformSubscription = sub),
              Reservations: (sub) => (this.locationReservationsSubscription = sub),
              Screens: (sub) => (this.locationScreensSubscription = sub),
              Connect: (sub) => (this.locationConnectSubscription = sub),
              Emno: (sub) => (this.locationEmnoSubscription = sub),
              Vfd: (sub) => (this.locationVfdSubscription = sub),
            };
      const set = (key: SubscriptionKey, value: AnySubscriptionModel | undefined): void => {
        setters[key](value ?? null);
      };

      set(
        'Visitors',
        subscriptions.find((sub) => sub.app === APP.VISITORS),
      );
      set(
        'Deliveries',
        subscriptions.find((sub) => sub.app === APP.DELIVERIES),
      );
      set(
        'Rooms',
        subscriptions.find((sub) => sub.app === APP.ROOMS),
      );
      set(
        'Desks',
        subscriptions.find((sub) => sub.app === APP.DESKS),
      );
      set(
        'Workplace',
        subscriptions.find((sub) => sub.app === APP.WORKPLACE),
      );
      set(
        'Platform',
        subscriptions.find((sub) => sub.app === APP.PLATFORM),
      );
      set(
        'Reservations',
        subscriptions.find((sub) => sub.app === APP.RESERVATIONS),
      );
      set(
        'Screens',
        featureFlags.isEnabled('wrkx-pricing-packaging')
          ? subscriptions.find((sub) => sub.app === APP.SCREENS)
          : // TODO: drop screens_devices fallback once wrkx-pricing-packaging is GA.
            subscriptions.find((sub) => sub.features.find((f) => f === 'screens_devices')),
      );
      set(
        'Connect',
        subscriptions.find((sub) => sub.app === APP.CONNECT),
      );
      set(
        'Emno',
        subscriptions.find((sub) => sub.app === APP.EMNO),
      );
      set(
        'Vfd',
        subscriptions.find((sub) => sub.app === APP.VFD),
      );
    }

    _assignRealSubscriptionFields(subscriptions: AnySubscriptionModel[]): void {
      // When a Workplace plan is activated, 'fake' subscriptions are created for Desks, Deliveries,
      // and Rooms in order to give the customer all the features the workplace plan is supposed to have.
      // Only 'Real' subs are displayed on the Billing page but feature gating uses both 'Real' and 'Fake' subs.
      this.realDeliveriesSubscription = subscriptions.find((sub) => sub.app === APP.DELIVERIES && !sub.faked) ?? null;
      this.realRoomsSubscription = subscriptions.find((sub) => sub.app === APP.ROOMS && !sub.faked) ?? null;
      this.realDesksSubscription = subscriptions.find((sub) => sub.app === APP.DESKS && !sub.faked) ?? null;
    }

    checkRestrictionsTask = restartableTask(async (refresh = true) => {
      const { currentCompany } = this;
      const { visitorsSubscription } = this as HostScopedReads;

      if (!visitorsSubscription) {
        return;
      }

      const locationLimit = parseFloat(visitorsSubscription.locationLimit);

      if (!visitorsSubscription.isSubscribed && locationLimit !== Infinity) {
        await this._getCompanyMeta.perform(currentCompany!, refresh);
      }
    });

    _getCompanyMeta = restartableTask(async (currentCompany: CompanyModel, refresh = false) => {
      if (this._companyMeta && !refresh) {
        return this._companyMeta;
      } else {
        const adapter = this.store.adapterFor('company');
        let meta = null;
        try {
          const url = `${adapter.buildURL('company', currentCompany.id)}?counts-meta=1`;
          const data = await this.ajax.request<{ meta: unknown }>(url);
          this._companyMeta = data.meta;
          meta = data.meta;
        } catch (_e) {
          // do nothing
        }
        return meta;
      }
    });

    setDeskLocationsTask = restartableTask(async () => {
      const { allDeskLocations, activeDeskLocations } = await this.getDeskLocations.perform();
      this.allDeskLocations = new TrackedArray(allDeskLocations);
      this.activeDeskLocations = new TrackedArray(activeDeskLocations);
    });

    /**
     * Whether desk-location data should be loaded for this company.
     * WRKX-5966: Also triggers for reservations-bundle customers (no standalone desks sub).
     */
    get shouldLoadDeskLocations(): boolean {
      if (!this.featureConfig.isEnabled('desks')) return false;
      const self = this as HostScopedReads;
      if (self.features?.canAccessDesksApplication) return true;
      if (self.desksSubscription) return true;
      if (this.featureFlags.isEnabled('wrkx-pricing-packaging') && self.reservationsSubscription) return true;
      return false;
    }

    getDeskLocations = restartableTask(async () => {
      try {
        const adapterOptions = { companyId: this.currentCompany?.id };
        const deskLocations = await this.store.findAll('desk-location', { reload: true, adapterOptions });

        let allDeskLocations: DeskLocationModel[] = [];
        let activeDeskLocations: DeskLocationModel[] = [];

        if (deskLocations?.length) {
          allDeskLocations = deskLocations.slice();
          activeDeskLocations = allDeskLocations.filter((dl) => dl.active);
        }

        return { allDeskLocations, activeDeskLocations };
      } catch {
        this.flashMessages.showFlash(
          'error',
          'Some of our backend systems are currently down.  Desks-related features may not work properly.  This should be resolved soon.',
        );
        return { allDeskLocations: [], activeDeskLocations: [] };
      }
    });
  }

  return withSubscriptionStateFunctionality;
}

export default withSubscriptionStateFunctionality;
